import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash, Trophy, XCircle } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Header, Disclaimer } from "../components/Header";
import { EvBadge } from "../components/ParlayCard";

export default function Saved() {
  const { API } = useAuth();
  const [items, setItems] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/parlays/saved`, { withCredentials: true });
      setItems(data);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const setResult = async (id, result) => {
    await axios.patch(`${API}/parlays/saved/${id}/result`, { result }, { withCredentials: true });
    load();
  };

  const remove = async (id) => {
    await axios.delete(`${API}/parlays/saved/${id}`, { withCredentials: true });
    toast.success("Removed");
    load();
  };

  const record = items?.reduce((acc, it) => {
    if (it.result === "won") acc.w += 1;
    if (it.result === "lost") acc.l += 1;
    return acc;
  }, { w: 0, l: 0 });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-slate-50 noise-bg">
      <Header />
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00E676] mb-2">History</p>
        <div className="flex items-end justify-between mb-8">
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">Saved parlays</h1>
          {record && (items?.length ?? 0) > 0 && (
            <p className="text-sm font-bold text-slate-400" data-testid="wl-record">
              Record: <span className="text-[#00E676]">{record.w}W</span> · <span className="text-[#FF3366]">{record.l}L</span>
            </p>
          )}
        </div>

        {items === null && <p className="text-slate-500">Loading...</p>}
        {items?.length === 0 && (
          <div className="text-center py-20 bg-[#141419] border border-white/10 rounded-xl" data-testid="saved-empty-state">
            <p className="text-slate-400 mb-4">No saved parlays yet.</p>
            <Link to="/app" className="rounded-full bg-[#00E676] text-black font-bold px-6 py-2.5 text-sm hover:scale-105 transition-transform inline-block">
              Build one now
            </Link>
          </div>
        )}

        <div className="space-y-4">
          {items?.map((it) => {
            const p = it.parlay;
            return (
              <div key={it.saved_id} className="bg-[#141419] border border-white/10 rounded-xl p-5" data-testid={`saved-parlay-${it.saved_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-2xl font-black">{p.combined_american}</span>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      {p.sport?.toUpperCase()} · {p.risk} · {p.num_legs} legs
                    </span>
                    <EvBadge ev={p.ev} />
                    {it.result !== "pending" && (
                      <span className={`text-xs font-black uppercase ${it.result === "won" ? "text-[#00E676]" : "text-[#FF3366]"}`}>
                        {it.result}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setResult(it.saved_id, it.result === "won" ? "pending" : "won")} data-testid={`mark-won-${it.saved_id}`}
                      className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${it.result === "won" ? "bg-[#00E676]/20 text-[#00E676]" : "bg-white/5 text-slate-500 hover:text-[#00E676]"}`}>
                      <Trophy size={16} weight="bold" />
                    </button>
                    <button onClick={() => setResult(it.saved_id, it.result === "lost" ? "pending" : "lost")} data-testid={`mark-lost-${it.saved_id}`}
                      className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${it.result === "lost" ? "bg-[#FF3366]/20 text-[#FF3366]" : "bg-white/5 text-slate-500 hover:text-[#FF3366]"}`}>
                      <XCircle size={16} weight="bold" />
                    </button>
                    <button onClick={() => remove(it.saved_id)} data-testid={`delete-saved-${it.saved_id}`}
                      className="h-9 w-9 rounded-full bg-white/5 text-slate-500 hover:text-white flex items-center justify-center transition-colors">
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                </div>
                <ul className="text-sm text-slate-400 space-y-1">
                  {p.legs?.map((l, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="truncate">{l.pick} <span className="text-slate-600">· {l.matchup}</span></span>
                      <span className="font-bold text-slate-300 shrink-0">{l.american_str}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </main>
      <Disclaimer />
    </div>
  );
}
